import { InitialEditModalState } from '@anm/components/stream/types';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LiveStreamKind } from 'types/stream';

import { streamActions, streamSelectors } from '../../../store/modules/stream';

type SubmitRecordingProps = {
  title: string;
  isEdit?: boolean;
  roomId?: string;
  folderId?: number;
  streamId?: string;
  isDuplicate?: boolean;
  streamInitialState?: InitialEditModalState;
};

const commonRecordingProps = {
  input: '',
  kind: 'RECORD' as LiveStreamKind,
  preview: '',
  description: '',
  destinationIds: []
};

const useSubmitRecording = ({
  title,
  roomId,
  isEdit,
  folderId,
  streamId,
  isDuplicate,
  streamInitialState
}: SubmitRecordingProps) => {
  const dispatch = useDispatch();
  const isNewRecordingPending = useSelector(streamSelectors.selectCreateStreamPending);

  const changeKindResponse = useSelector(streamSelectors.selectChangeStreamKindResponse);
  const canUpdateRecord =
    changeKindResponse?.kind === 'RECORD' && changeKindResponse?.streamId === streamId && changeKindResponse;

  const handleUpdateRecording = useCallback(() => {
    streamId &&
      dispatch(
        streamActions.editStream({
          title,
          folderId,
          streamId,
          schedule: null,
          ...commonRecordingProps
        })
      );
  }, [title, folderId, streamId]);

  useEffect(() => {
    if (!canUpdateRecord) return;

    handleUpdateRecording();
    dispatch(streamActions.cleanStreamKind());
  }, [streamId, canUpdateRecord]);

  const handleEditRecording = useCallback(() => {
    streamId &&
      dispatch(
        streamActions.editStream({
          title,
          folderId,
          streamId,
          ...commonRecordingProps
        })
      );
  }, [title, folderId, streamId]);

  const action = isDuplicate ? streamActions.duplicateStream : streamActions.createStream;

  const handleCreateNewRecording = useCallback(() => {
    dispatch(
      action({
        title,
        roomId,
        folderId,
        isDuplicate,
        schedule: null,
        ...commonRecordingProps
      })
    );
  }, [folderId, isDuplicate, title]);

  const handleConvertLiveToRecord = useCallback(() => {
    streamId && dispatch(streamActions.changeStreamKind({ streamId, kind: 'RECORD' }));
  }, [streamId]);

  const canConvertToRecord = streamInitialState?.kind === 'LIVE' && !isDuplicate;

  const handleSubmit = canConvertToRecord
    ? handleConvertLiveToRecord
    : isEdit && !isDuplicate
    ? handleEditRecording
    : handleCreateNewRecording;

  return [isNewRecordingPending, handleSubmit] as const;
};

export default useSubmitRecording;
